import React from 'react';

/* =========================================================
   Solution illustrations.

   Hand-drawn SVGs, one per solution. Lifted out of the Conversational AI page
   so the solution pages and the product page draw from the same set — they
   were the only unique asset on the retired Customer AI page and are worth
   keeping in one place.
   ========================================================= */

/* ---------- product-card illustrations ---------- */

// AI Receptionist — a phone on an in-call screen.
export const PhoneGraphic = () => (
  <svg viewBox="0 0 160 150" className="h-[130px]" role="img" aria-label="Phone call">
    <rect x="55" y="10" width="50" height="130" rx="11" fill="#fff" stroke="#99f6e4" strokeWidth="2" />
    <rect x="60" y="18" width="40" height="114" rx="7" fill="#f0fdfa" />
    <circle cx="80" cy="44" r="13" fill="#ccfbf1" />
    <circle cx="80" cy="39" r="4.8" fill="#0d9488" />
    <path d="M71 52a9 8 0 0 1 18 0z" fill="#0d9488" />
    <rect x="66" y="64" width="28" height="4.5" rx="2.25" fill="#5eead4" />
    <rect x="70" y="73" width="20" height="3.5" rx="1.75" fill="#99f6e4" />
    <g fill="#2dd4bf">
      {[0, 1, 2, 3, 4].map((i) => {
        const h = [8, 16, 22, 14, 9][i];
        return <rect key={i} x={65 + i * 6.4} y={94 - h / 2} width="3.4" height={h} rx="1.7" />;
      })}
    </g>
    <circle cx="72" cy="120" r="7.5" fill="#22c55e" />
    <path d="M69 120a3 3 0 0 1 6 0" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="88" cy="120" r="7.5" fill="#ef4444" />
    <path d="M85 120h6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// Customer Service — two chat bubbles with avatars.
export const ChatGraphic = () => (
  <svg viewBox="0 0 160 150" className="h-[120px]" role="img" aria-label="Chat conversation">
    <circle cx="30" cy="52" r="11" fill="#bbf7d0" />
    <circle cx="30" cy="49" r="3.8" fill="#16a34a" />
    <path d="M23 58a7 6 0 0 1 14 0z" fill="#16a34a" />
    <rect x="48" y="40" width="92" height="30" rx="9" fill="#fff" stroke="#d5e8dc" strokeWidth="1.5" />
    <rect x="58" y="50" width="52" height="4.5" rx="2.25" fill="#cfe9d8" />
    <rect x="58" y="59" width="34" height="4.5" rx="2.25" fill="#e4f3ea" />
    <circle cx="130" cy="104" r="11" fill="#99f6e4" />
    <circle cx="130" cy="101" r="3.8" fill="#0d9488" />
    <path d="M123 110a7 6 0 0 1 14 0z" fill="#0d9488" />
    <rect x="20" y="92" width="92" height="30" rx="9" fill="#f0fdfa" stroke="#99f6e4" strokeWidth="1.5" />
    <rect x="30" y="102" width="60" height="4.5" rx="2.25" fill="#5eead4" />
    <rect x="30" y="111" width="40" height="4.5" rx="2.25" fill="#ccfbf1" />
  </svg>
);

// Call Center — a headset.
export const HeadsetGraphic = () => (
  <svg viewBox="0 0 160 150" className="h-[116px]" role="img" aria-label="Headset">
    <path d="M40 96V74a40 40 0 0 1 80 0v22" fill="none" stroke="#7c3aed" strokeWidth="7" strokeLinecap="round" />
    <rect x="30" y="88" width="20" height="34" rx="9" fill="#8b5cf6" />
    <rect x="110" y="88" width="20" height="34" rx="9" fill="#8b5cf6" />
    <path d="M40 118v4a14 14 0 0 0 14 14h10" fill="none" stroke="#7c3aed" strokeWidth="5" strokeLinecap="round" />
    <circle cx="66" cy="136" r="5.5" fill="#8b5cf6" />
  </svg>
);

// Appointment Scheduling — a calendar card with time slots.
export const CalendarGraphic = () => (
  <svg viewBox="0 0 170 150" className="h-[126px]" role="img" aria-label="Calendar">
    <rect x="26" y="16" width="118" height="118" rx="12" fill="#fff" stroke="#f0dfc4" strokeWidth="1.6" />
    <rect x="26" y="16" width="118" height="26" rx="12" fill="#fbeed2" />
    <rect x="26" y="30" width="118" height="12" fill="#fbeed2" />
    <text x="85" y="34" textAnchor="middle" fontSize="11" fontWeight="700" fill="#b4813a">June 2025</text>
    <g fill="#d9c39a">
      {Array.from({ length: 7 }).map((_, c) => (
        <circle key={`h${c}`} cx={40 + c * 15.5} cy={54} r="2" />
      ))}
    </g>
    <g fontSize="8" fill="#64748b" textAnchor="middle">
      {Array.from({ length: 21 }).map((_, i) => {
        const r = Math.floor(i / 7);
        const c = i % 7;
        const d = i + 1;
        const cx = 40 + c * 15.5;
        const cy = 70 + r * 16;
        const sel = d === 12;
        return (
          <g key={d}>
            {sel && <circle cx={cx} cy={cy - 3} r="8.5" fill="#0d9488" />}
            <text x={cx} y={cy} fontSize="8" fill={sel ? '#fff' : '#64748b'} fontWeight={sel ? 700 : 400}>{d}</text>
          </g>
        );
      })}
    </g>
    <rect x="40" y="118" width="42" height="12" rx="6" fill="#ccfbf1" />
    <text x="61" y="126.5" textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#0d9488">10:00 AM</text>
    <rect x="88" y="118" width="42" height="12" rx="6" fill="#f0fdfa" />
    <text x="109" y="126.5" textAnchor="middle" fontSize="7.5" fontWeight="700" fill="#2dd4bf">11:00 AM</text>
  </svg>
);

// Research & Polling — a donut chart with bars.
export const ChartGraphic = () => {
  const cx = 52;
  const cy = 58;
  const r = 22;
  const seg = (from, to, color) => {
    const a0 = (from / 100) * 2 * Math.PI - Math.PI / 2;
    const a1 = (to / 100) * 2 * Math.PI - Math.PI / 2;
    const large = to - from > 50 ? 1 : 0;
    return (
      <path
        d={`M${cx + r * Math.cos(a0)},${cy + r * Math.sin(a0)} A${r},${r} 0 ${large} 1 ${cx + r * Math.cos(a1)},${cy + r * Math.sin(a1)} L${cx},${cy} Z`}
        fill={color}
      />
    );
  };
  return (
    <svg viewBox="0 0 170 150" className="h-[118px]" role="img" aria-label="Analytics chart">
      <rect x="14" y="16" width="142" height="118" rx="12" fill="#fff" stroke="#cfe6ee" strokeWidth="1.6" />
      {seg(0, 45, '#0d9488')}
      {seg(45, 72, '#38bdf8')}
      {seg(72, 100, '#14b8a6')}
      <circle cx={cx} cy={cy} r="10" fill="#fff" />
      <g>
        <rect x="96" y="40" width="46" height="5" rx="2.5" fill="#ccfbf1" />
        <rect x="96" y="52" width="34" height="5" rx="2.5" fill="#e2e8f0" />
        <rect x="96" y="64" width="40" height="5" rx="2.5" fill="#e2e8f0" />
      </g>
      <g>
        {[18, 30, 24, 38, 44].map((h, i) => (
          <rect key={i} x={30 + i * 22} y={122 - h} width="12" height={h} rx="2.5" fill={i % 2 ? '#38bdf8' : '#0d9488'} />
        ))}
      </g>
    </svg>
  );
};
